// src/ui/SupporterBadge.jsx — the AlloApp Supporter badge (web). The same drawing as the app's
// src/components/SupporterBadge.tsx: a soft twelve-lobed seal in the tier's colours with a white HEART.
//
// 🟥 NEVER A CHECK MARK. AlloApp already draws verified accounts as a blue scalloped seal with a tick
//    (VerifiedSeal). A paid badge in the same shape would read as "verified" — the confusion the plan (§6.5) warns
//    of, and the one other social apps were criticised for. A heart says "supports AlloApp", nothing about identity.
// 🟢 Colour is not the only difference (colour-blind readers): Super Supporter adds an inner ring, Champion a spark.
import React from 'react';
import { subscribeSupporters, supporterOf } from '../services/supporters';

export const TIER_COLORS = {
  blue: ['#2F80ED', '#56A8FF'],
  purple: ['#8E44EC', '#B57CFF'],
  orange: ['#FF4F2E', '#FF9A3D'],
};
export const TIER_LABEL = {
  en: { blue: 'Supporter', purple: 'Super Supporter', orange: 'Champion' },
  ar: { blue: 'داعم', purple: 'داعم مميز', orange: 'بطل' },
};
const LOBES = 12;
export const SUPPORTER_SEAL = (() => {
  let d = '';
  for (let i = 0; i <= 180; i += 1) {
    const a = (i / 180) * Math.PI * 2 - Math.PI / 2;
    const r = 43 + 5 * Math.cos(LOBES * a);
    d += `${i === 0 ? 'M' : 'L'}${(50 + r * Math.cos(a)).toFixed(2)} ${(50 + r * Math.sin(a)).toFixed(2)}`;
  }
  return `${d}Z`;
})();
export const HEART = 'M50 72 C50 72 27 58 27 43 C27 34.5 33.5 29 41 29 C45.5 29 48.6 31.6 50 34.6 C51.4 31.6 54.5 29 59 29 C66.5 29 73 34.5 73 43 C73 58 50 72 50 72 Z';
export const SPARK = 'M80 9 L82.4 16.6 L90 19 L82.4 21.4 L80 29 L77.6 21.4 L70 19 L77.6 16.6 Z';

export function SupporterSeal({ tier, size = 14, title }) {
  const c = TIER_COLORS[tier];
  if (!c) return null;
  const id = `sg_${tier}`;
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" role="img" aria-label={title} style={{ flex: '0 0 auto', verticalAlign: '-0.15em' }}>
      {title ? <title>{title}</title> : null}
      <defs><linearGradient id={id} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor={c[0]} /><stop offset="1" stopColor={c[1]} /></linearGradient></defs>
      <path d={SUPPORTER_SEAL} fill={`url(#${id})`} />
      {tier === 'purple' ? <circle cx="50" cy="50" r="34" fill="none" stroke="#FFFFFF" strokeOpacity="0.55" strokeWidth="3" /> : null}
      <path d={HEART} fill="#FFFFFF" />
      {tier === 'orange' ? <path d={SPARK} fill="#FFFFFF" stroke={c[0]} strokeWidth="1.5" /> : null}
    </svg>
  );
}

/** the badge of a user (from the shared, cached directory — no read per name) */
export default function SupporterBadge({ uid, size = 14, lang = 'en', style }) {
  const [, force] = React.useState(0);
  React.useEffect(() => subscribeSupporters(() => force((n) => n + 1)), []);
  const s = supporterOf(uid);
  if (!s) return null;
  const label = `AlloApp ${(TIER_LABEL[lang] || TIER_LABEL.en)[s.tier] || ''}`;
  return <span className="supporter-badge" style={{ display: 'inline-flex', marginInlineStart: 4, ...(style || {}) }}><SupporterSeal tier={s.tier} size={size} title={label} /></span>;
}
